// ══════════════════════════════════════════════════════════════════
//  HomePage.jsx
// ══════════════════════════════════════════════════════════════════
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSafeRedirect, authPathWithRedirect } from '../utils/safeRedirect';
import toast from 'react-hot-toast';
import ListingCard from '../components/listings/ListingCard';
import SecondFactorPrompt from '../components/SecondFactorPrompt';
import SocialLogin from '../components/SocialLogin';
import { useSEO } from '../hooks/useSEO';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useInviteGate } from '../context/InviteGateContext';

// Coinbase Commerce (crypto) checkout is hidden unless REACT_APP_COINBASE_CHECKOUT_ENABLED=true at build time.
const COINBASE_CHECKOUT_ENABLED = process.env.REACT_APP_COINBASE_CHECKOUT_ENABLED === 'true';

export function HomePage() {
  useSEO({});
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const [listings,    setListings]    = useState({ rent:[], auction:[], sale:[], barter:[], free:[] });
  const [featured,    setFeatured]    = useState([]);
  const [trending,    setTrending]    = useState([]);
  const [promoted,    setPromoted]    = useState([]);
  const [categoryHighlights, setCategoryHighlights] = useState({});
  const [newItems,    setNewItems]    = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [newItemsLabel, setNewItemsLabel] = useState('New This Week');
  const [trendingView, setTrendingView] = useState('trending'); // 'trending' or 'new'
  const [selectedCatId, setSelectedCatId] = useState(null);
  const [query,       setQuery]       = useState('');
  const [activeCat,   setActiveCat]   = useState('All');
  const [categories,  setCategories]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestName, setSuggestName] = useState('');
  const [suggestIcon, setSuggestIcon] = useState('📦');
  const [suggestDone, setSuggestDone] = useState(false);
  const [suggestErr,  setSuggestErr]  = useState('');
  const [platformStats, setPlatformStats] = useState({ listingCount: 0, avgRating: null, userCount: 0 });
  const [displayStats,  setDisplayStats]  = useState({ listingCount: 0, avgRating: 0, userCount: 0 });
  const statsAnimRef = useRef(null);
  const [recentlyViewed, setRecentlyViewed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cherrypkd_rv') || '[]'); } catch { return []; }
  });
  const [rvActiveIds, setRvActiveIds] = useState(null); // null = not yet checked
  const [forYou, setForYou] = useState([]);
  const [endingSoon, setEndingSoon] = useState([]);
  const [endingSoonTick, setEndingSoonTick] = useState(0);
  const [flashDeals, setFlashDeals] = useState([]);
  const [followedSellers, setFollowedSellers] = useState([]);

  const ICONS = ['📦','🛠️','🎯','🧸','🌿','🔬','🎭','🏠','🚀','💎','🌊','🎪','🔮','🧲','⚡','🌺','🦋','🎵','🏺','⚙️'];

  useEffect(() => {
    if (recentlyViewed.length > 0) {
      const ids = recentlyViewed.map(l => l.id).join(',');
      API.get(`/listings/active-check?ids=${ids}`)
        .then(r => setRvActiveIds(new Set(r.data.activeIds)))
        .catch(() => setRvActiveIds(new Set()));
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    const majorCategories = ['electronics', 'home-garden', 'fashion', 'sports', 'collectibles'];

    // Try to get user's location for location-based new items
    let userLat, userLng, cityName;

    if (user?.lat && user?.lng) {
      // Use logged-in user's saved location
      userLat = user.lat;
      userLng = user.lng;
      cityName = user.locationCity;
    }

    // Load core data first (10 requests)
    const coreRequests = [
      API.get('/listings?type=rent&limit=4'),
      API.get('/listings?type=auction&limit=4'),
      API.get('/listings?type=sale&limit=4'),
      API.get('/barters/listings?limit=4'),
      API.get('/free/listings?limit=4'),
      API.get('/search/featured?limit=8'),
      API.get('/search/trending?limit=8'),
      API.get('/categories'),
      API.get('/promotions/active?limit=12'),
    ];

    // Add location-based new items if user has location
    let newItemsUrl = '/search/new?limit=8&days=7';
    if (userLat && userLng) {
      newItemsUrl += `&lat=${userLat}&lng=${userLng}&distance=25`;
    }
    coreRequests.push(API.get(newItemsUrl));
    coreRequests.push(API.get('/search/ending-soon?hours=24&limit=6'));
    coreRequests.push(API.get('/search/listings?onSale=true&limit=8&sort=newest'));

    Promise.allSettled(coreRequests).then(async (results) => {
      const [r, a, s, b, fr, f, t, cats, promo, n, es, fd] = results;
      setEndingSoon(es?.status === 'fulfilled' ? es.value.data.results || [] : []);
      setFlashDeals(fd?.status === 'fulfilled' ? fd.value.data.results || [] : []);
      setListings({
        rent: r.status === 'fulfilled' ? r.value.data.listings : [],
        auction: a.status === 'fulfilled' ? a.value.data.listings : [],
        sale: s.status === 'fulfilled' ? s.value.data.listings : [],
        barter: b.status === 'fulfilled' ? b.value.data.listings : [],
        free: fr.status === 'fulfilled' ? fr.value.data.listings : []
      });
      setFeatured(f.status === 'fulfilled' ? f.value.data.results || [] : []);
      setTrending(t.status === 'fulfilled' ? t.value.data.results || [] : []);
      setPromoted(promo.status === 'fulfilled' ? promo.value.data.promotions || [] : []);
      setNewItems(n.status === 'fulfilled' ? n.value.data.results || [] : []);

      if (userLat && userLng) {
        setUserLocation({ lat: userLat, lng: userLng });
        setNewItemsLabel(`New in ${cityName || 'Your Area'}`);
      }

      const catData = cats.status === 'fulfilled' ? cats.value.data.categories : [];
      setCategories(catData);
      setLoading(false);

      // Load "For You" feed — server-side when logged in, localStorage for guests
      try {
        if (user?.id) {
          const { data: fyData } = await API.get('/search/for-you?limit=16');
          if (fyData.results?.length >= 3) { setForYou(fyData.results); }
        } else {
          const rv = JSON.parse(localStorage.getItem('cherrypkd_rv') || '[]');
          if (rv.length > 0) {
            const seenIds = new Set(rv.map(l => l.id));
            const catSlugs = [...new Set(rv.map(l => l.category_slug || l.category_id).filter(Boolean))].slice(0, 3);
            if (catSlugs.length > 0) {
              const results = await Promise.allSettled(
                catSlugs.map(slug => API.get(`/listings?category=${slug}&limit=6&sort=newest`))
              );
              const merged = results.flatMap(r =>
                r.status === 'fulfilled' ? (r.value.data.listings || []) : []
              ).filter(l => !seenIds.has(l.id));
              const unique = merged.filter((l, i, a) => a.findIndex(x => x.id === l.id) === i).slice(0, 8);
              if (unique.length >= 3) setForYou(unique);
            }
          }
        }
      } catch {}

      // Load category highlights — try trending (7-day window), fall back to newest
      const slugsToTry = [
        ...majorCategories,
        ...catData.map(c => c.slug).filter(s => !majorCategories.includes(s)),
      ];
      let delayMs = 0;
      let loaded = 0;
      slugsToTry.forEach((catSlug) => {
        if (loaded >= 5) return; // cap at 5 categories
        setTimeout(async () => {
          try {
            const catInfo = catData.find(c => c.slug === catSlug);
            if (!catInfo) return;

            // Try trending with a 7-day window first
            let items = [];
            try {
              const { data } = await API.get(`/search/trending?category=${catSlug}&limit=4&days=7`);
              items = data.results || [];
            } catch {}

            // Fall back to newest listings in that category
            if (items.length === 0) {
              const { data } = await API.get(`/search/listings?category=${catSlug}&limit=4&sort=newest`);
              items = data.results || [];
            }

            if (items.length > 0) {
              loaded++;
              setCategoryHighlights(prev => ({ ...prev, [catSlug]: { ...catInfo, items } }));
            }
          } catch (err) {
            console.error(`Failed to load category ${catSlug}:`, err);
          }
        }, delayMs);
        delayMs += 400;
      });
    });
  }, []);

  // Set initial category when highlights load
  useEffect(() => {
    if (!selectedCatId && Object.keys(categoryHighlights).length > 0) {
      const firstCat = Object.keys(categoryHighlights)[0];
      setSelectedCatId(firstCat);
    }
  }, [categoryHighlights]);

  // Load "From Sellers You Follow" — logged-in users only
  useEffect(() => {
    if (!user) return;
    API.get('/users/me/following')
      .then(r => {
        const sellers = r.data.following || [];
        const withListings = sellers.filter(s => s.recent_listings?.length > 0);
        if (withListings.length >= 1) setFollowedSellers(withListings.slice(0, 6));
      })
      .catch(() => {});
  }, [user?.id]); // eslint-disable-line

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'superadmin') {
      API.get('/admin/suggestions')
        .then(r => setSuggestions(r.data.suggestions || []))
        .catch(() => {});
    }
  }, [user]);

  // Countdown tick for ending-soon section
  useEffect(() => {
    if (!endingSoon.length) return;
    const iv = setInterval(() => setEndingSoonTick(t => t + 1), 1000);
    return () => clearInterval(iv);
  }, [endingSoon.length]);

  const handlePromote = async (s) => {
    try {
      await API.post(`/categories/${s.id}/promote`);
      setSuggestions(prev => prev.filter(x => x.id !== s.id));
      API.get('/categories').then(r => setCategories(r.data.categories));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Promote failed');
    }
  };

  const handleSuggest = async () => {
    if (!suggestName.trim()) return;
    setSuggestErr('');
    try {
      await API.post('/categories/suggest', { label: suggestName.trim(), icon: suggestIcon });
      setSuggestDone(true);
      setSuggestName('');
    } catch (err) {
      setSuggestErr(err.response?.data?.error || 'Could not submit — please sign in first');
    }
  };

  useEffect(() => {
    API.get('/listings/platform-stats').then(r => {
      setPlatformStats({ ...r.data, loaded: true });
      // count-up animation over 1.5s
      const targets = r.data;
      const duration = 1500;
      const steps = 60;
      const interval = duration / steps;
      let step = 0;
      clearInterval(statsAnimRef.current);
      statsAnimRef.current = setInterval(() => {
        step++;
        const pct = step / steps;
        const ease = 1 - Math.pow(1 - pct, 3); // ease-out cubic
        setDisplayStats({
          listingCount: Math.round((targets.listingCount || 0) * ease),
          avgRating:    Math.round(((targets.avgRating || 4.9) * ease) * 10) / 10,
          userCount:    Math.round((targets.userCount || 0) * ease),
        });
        if (step >= steps) clearInterval(statsAnimRef.current);
      }, interval);
    }).catch(() => setPlatformStats(s => ({ ...s, loaded: true })));
    return () => clearInterval(statsAnimRef.current);
  }, []);

  const handleSearch = () => {
    if (query.trim()) navigate(`/browse?q=${encodeURIComponent(query)}`);
  };

  const fmtNum = n => n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k+` : `${n}+`;
  // Neutral placeholders until /listings/platform-stats hydrates — never flash fake marketing counts.
  const stats = [
    { num: platformStats.listingCount > 0 ? fmtNum(displayStats.listingCount) : '—', label: 'Listings' },
    { num: platformStats.avgRating    != null ? `${displayStats.avgRating.toFixed(1)}★` : '—',   label: 'Avg Rating' },
    { num: '100%',                                                                                    label: 'Secure Escrow' },
    { num: platformStats.userCount    > 0 ? fmtNum(displayStats.userCount)    : '—',            label: 'Happy Users' },
  // QA r3: '—' is only a loading placeholder. Once /listings/platform-stats has answered, drop any
  // counter with no data (e.g. no reviews yet → no avg rating) instead of showing a dash.
  ].filter(s => !platformStats.loaded || s.num !== '—');
  const hiw = [
    { icon:'🔍', num:'01', title:'Find what you need', desc:'Search thousands of local listings across rentals, auctions, and sales.' },
    { icon:'🔒', num:'02', title:'Pay safely via escrow', desc: COINBASE_CHECKOUT_ENABLED ? "Payment held securely — card or crypto. Released only when you're satisfied." : "Payment held securely by card. Released only when you're satisfied." },
    { icon:'📍', num:'03', title:'Meet at a safe spot', desc:'Owners set public meetup locations. Safe, neutral handovers every time.' },
    { icon:'✅', num:'04', title:'Complete the deal', desc:'Confirm the item arrived or the pickup is done, payment is released, deposit returned. Leave a review.' },
  ];

  return (
    <div>
      {/* Hero */}
      <div className="hero">
        <div className="hero-content">
          <div className="hero-eyebrow"><span className="hero-pulse"/>Soft launch · Invite only</div>
          <h1 className="hero-h1">Rent it. <em>Bid on it.</em> Buy it.</h1>
          <p className="hero-sub">The marketplace built on trust. Borrow, discover, and buy quality items from real people in your community.</p>
          <div className="hero-cta">
            <button className="btn btn-cherry btn-lg" onClick={() => navigate('/browse')}>🔍 Browse Listings</button>
            <button className="btn btn-lg" style={{ background:'transparent',color:'white',border:'1.5px solid rgba(255,255,255,.4)' }} onClick={() => navigate('/list')}>📋 List an Item</button>
          </div>
          <div className="hero-search">
            <input className="hero-search-input" placeholder="Search cameras, tools, furniture..." value={query} onChange={e=>setQuery(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleSearch()}/>
            <button className="hero-search-btn" onClick={handleSearch}>Search</button>
          </div>
          <div className="hero-stats">
            {stats.map(s=><div key={s.label} className="hero-stat"><div className="hero-stat-num">{s.num}</div><div className="hero-stat-label">{s.label}</div></div>)}
          </div>
        </div>
      </div>

      {/* Category strip */}
      <div className="cat-strip">
        <div className="cat-list" style={{ padding:'0 clamp(16px,4vw,48px)' }}>
          <button key="All" className={`cat-btn ${activeCat==='All'?'active':''}`}
            onClick={() => { setActiveCat('All'); navigate('/browse'); }}>
            ✨ All
          </button>
          {categories.map(cat => (
            <button key={cat.slug} className={`cat-btn ${activeCat===cat.label?'active':''}`}
              onClick={() => { setActiveCat(cat.label); navigate(`/categories/${cat.slug}`); }}>
              {cat.icon} {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Admin: pending category suggestions banner */}
      {suggestions.length > 0 && (
        <div style={{ maxWidth:1280, margin:'24px auto 0', padding:'0 clamp(16px,4vw,48px)' }}>
          <div style={{ background:'linear-gradient(135deg,#1A1A1A,#2C2C2C)', borderRadius:14, padding:'18px 24px', display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
            <span style={{ fontSize:24 }}>🏷️</span>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'white', marginBottom:10 }}>
                Pending category suggestions
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {suggestions.map(s => (
                  <div key={s.id} style={{ background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.12)', borderRadius:8, padding:'6px 12px', display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:16 }}>{s.icon}</span>
                    <div>
                      <div style={{ fontSize:12, fontWeight:700, color:'white' }}>{s.label}</div>
                      <div style={{ fontSize:10, color:'#888' }}>{s.vote_count} vote{s.vote_count!==1?'s':''}</div>
                    </div>
                    <button
                      onClick={() => handlePromote(s)}
                      style={{ padding:'3px 10px', borderRadius:5, border:'none', background:'#2D7A3A', color:'white', fontSize:10, fontWeight:700, cursor:'pointer' }}>
                      ✅ Promote
                    </button>
                    <button
                      onClick={() => setSuggestions(prev => prev.filter(x => x.id !== s.id))}
                      style={{ padding:'3px 7px', borderRadius:5, border:'none', background:'rgba(255,255,255,.1)', color:'#aaa', fontSize:10, cursor:'pointer' }}>
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Featured, Trending & New Sections */}
      <div style={{ maxWidth:1280,margin:'0 auto',padding:'56px clamp(16px,4vw,48px) 0' }}>

        {/* Ending Soon — live auction countdowns */}
        {endingSoon.length > 0 && (
          <section style={{ paddingBottom:56 }}>
            <div style={{ display:'flex',alignItems:'flex-end',justifyContent:'space-between',marginBottom:24 }}>
              <div>
                <div className="section-eyebrow" style={{ color:'var(--cherry)' }}>⏰ Don't miss out</div>
                <h2 className="section-title">Auctions Ending Soon</h2>
              </div>
              <button type="button" style={{ fontSize:14,fontWeight:600,color:'var(--cherry)',cursor:'pointer',background:'none',border:'none',padding:0 }} onClick={()=>navigate('/browse?type=auction')}>See all auctions →</button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:16 }}>
              {endingSoon.map((l, i) => {
                const msLeft = new Date(l.auction_ends_at) - Date.now();
                const hrs = Math.max(0, Math.floor(msLeft / 3600000));
                const mins = Math.max(0, Math.floor((msLeft % 3600000) / 60000));
                const secs = Math.max(0, Math.floor((msLeft % 60000) / 1000));
                const urgent = msLeft < 3600000; // < 1 hour
                return (
                  <a key={l.id} href={`/listing/${l.slug || l.id}`} style={{ position:'relative', display:'block', textDecoration:'none', color:'inherit' }}>
                    <ListingCard listing={l} delay={i * .07} />
                    <div style={{
                      position:'absolute', top:8, left:8,
                      background: urgent ? 'var(--cherry)' : '#1A1A1A',
                      color:'white', padding:'5px 12px', borderRadius:20,
                      fontSize:12, fontWeight:800, letterSpacing:'.5px',
                      boxShadow:'0 2px 10px rgba(0,0,0,.3)',
                      fontVariantNumeric:'tabular-nums',
                      fontFamily:'monospace',
                    }}>
                      {urgent && '🔴 '}
                      {hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m ${secs < 10 ? '0' : ''}${secs}s`}
                    </div>
                  </a>
                );
              })}
            </div>
          </section>
        )}

        {/* Promoted Listings (VIP, Premium, Basic tiers) */}
        {promoted.length > 0 && (
          <section style={{ paddingBottom:56 }}>
            <div style={{ display:'flex',alignItems:'flex-end',justifyContent:'space-between',marginBottom:24 }}>
              <div><div className="section-eyebrow">⭐ Premium Listings</div><h2 className="section-title">Promoted by Sellers</h2></div>
            </div>
            <div className="listings-grid">
              {promoted.slice(0,8).map((l,i)=>{
                const tierBadge = { vip:'🥇 VIP', premium:'🥈 Premium', basic:'🥉 Basic' }[l.tier];
                return (
                  <div key={l.id} style={{ position:'relative' }}>
                    <ListingCard listing={l} delay={i*.07}/>
                    <div style={{
                      position:'absolute',
                      top:8,
                      left:8,
                      background:'linear-gradient(135deg, #FFD700, #FFA500)',
                      color:'#1C1C1C',
                      padding:'4px 10px',
                      borderRadius:20,
                      fontSize:11,
                      fontWeight:700,
                      boxShadow:'0 2px 8px rgba(0,0,0,0.15)'
                    }}>
                      {tierBadge}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Featured (Admin-curated) */}
        {featured.length > 0 && (
          <section style={{ paddingBottom:56 }}>
            <div style={{ display:'flex',alignItems:'flex-end',justifyContent:'space-between',marginBottom:24 }}>
              <div><div className="section-eyebrow">⭐ Editor's Picks</div><h2 className="section-title">Featured Items</h2></div>
              <button type="button" style={{ fontSize:14,fontWeight:600,color:'var(--cherry)',cursor:'pointer',background:'none',border:'none',padding:0 }} onClick={()=>navigate('/browse')}>Browse all →</button>
            </div>
            <div className="listings-grid">
              {featured.slice(0,8).map((l,i)=><ListingCard key={l.id} listing={l} delay={i*.07}/>)}
            </div>
          </section>
        )}

        {/* Flash Deals */}
        {flashDeals.length > 0 && (
          <section style={{ paddingBottom:56 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:16 }}>
              <div>
                <div className="section-eyebrow">⚡ Limited time</div>
                <h2 className="section-title" style={{ display:'flex', alignItems:'center', gap:10 }}>
                  Flash Deals
                  <span style={{ background:'linear-gradient(135deg,#C0392B,#E74C3C)', color:'white', fontSize:11, fontWeight:800, padding:'4px 10px', borderRadius:20, letterSpacing:'0.5px' }}>
                    🔥 ON SALE
                  </span>
                </h2>
              </div>
              <button type="button" style={{ fontSize:14, fontWeight:600, color:'var(--cherry)', cursor:'pointer', background:'none', border:'none', padding:0 }} onClick={() => navigate('/browse?onSale=true')}>View all deals →</button>
            </div>
            <div className="listings-grid">
              {flashDeals.map((l,i) => <ListingCard key={l.id} listing={l} delay={i*.07} />)}
            </div>
          </section>
        )}

        {/* Trending vs New Toggle */}
        {(trending.length > 0 || newItems.length > 0) && (
          <section style={{ paddingBottom:56 }}>
            <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24,flexWrap:'wrap',gap:16 }}>
              <div style={{ display:'flex',alignItems:'center',gap:16 }}>
                <div><div className="section-eyebrow">{trendingView === 'trending' ? '🔥 Hot right now' : '✨ Just posted'}</div><h2 className="section-title">{trendingView === 'trending' ? 'Trending Today' : newItemsLabel}</h2></div>
                <div style={{ display:'flex',gap:4,background:'var(--cream)',borderRadius:8,padding:4 }}>
                  <button onClick={() => setTrendingView('trending')} style={{ padding:'6px 16px',borderRadius:6,border:'none',background:trendingView==='trending'?'white':'transparent',color:trendingView==='trending'?'var(--cherry)':'var(--text-soft)',cursor:'pointer',fontWeight:600,fontSize:13,transition:'all .2s' }}>🔥 Trending</button>
                  <button onClick={() => setTrendingView('new')} style={{ padding:'6px 16px',borderRadius:6,border:'none',background:trendingView==='new'?'white':'transparent',color:trendingView==='new'?'var(--cherry)':'var(--text-soft)',cursor:'pointer',fontWeight:600,fontSize:13,transition:'all .2s' }}>✨ New</button>
                </div>
              </div>
              <button type="button" className="tap-target" style={{ fontSize:14,fontWeight:600,color:'var(--cherry)',cursor:'pointer',background:'none',border:'none',padding:0 }} onClick={()=>navigate(trendingView==='trending'?'/browse?sort=trending':'/browse?daysPosted=7')}>View all →</button>
            </div>
            <div className="listings-grid">
              {(trendingView === 'trending' ? trending : newItems).slice(0,8).map((l,i)=><ListingCard key={l.id} listing={l} delay={i*.07}/>)}
            </div>
          </section>
        )}

        {/* Category Highlights - TABBED */}
        {Object.keys(categoryHighlights).length > 0 && (
          <section style={{ paddingBottom:56 }}>
            <div style={{ marginBottom:24 }}>
              <div style={{ marginBottom:16 }}><div className="section-eyebrow">🌟 Hot in Categories</div><h2 className="section-title">Trending by Category</h2></div>
              <div style={{ display:'flex',gap:8,overflowX:'auto',paddingBottom:12 }}>
                {Object.entries(categoryHighlights).map(([slug, cat]) => (
                  <button
                    key={slug}
                    onClick={() => setSelectedCatId(slug)}
                    style={{
                      padding:'8px 16px',
                      borderRadius:8,
                      border:'none',
                      background:selectedCatId===slug?'var(--cherry)':'var(--white)',
                      color:selectedCatId===slug?'white':'var(--text-mid)',
                      cursor:'pointer',
                      fontWeight:600,
                      fontSize:13,
                      whiteSpace:'nowrap',
                      transition:'all .2s',
                      flexShrink:0
                    }}
                  >
                    {cat.icon} {cat.label}
                  </button>
                ))}
              </div>
            </div>
            {selectedCatId && categoryHighlights[selectedCatId] && (
              <div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                  <h3 style={{ fontSize:16,fontWeight:700 }}>
                    {categoryHighlights[selectedCatId].icon} Trending in {categoryHighlights[selectedCatId].label}
                  </h3>
                  <button
                    type="button"
                    style={{ fontSize:14,fontWeight:600,color:'var(--cherry)',cursor:'pointer',background:'none',border:'none',padding:0 }}
                    onClick={() => navigate(`/categories/${selectedCatId}`)}
                  >
                    Browse all →
                  </button>
                </div>
                <div className="listings-grid">
                  {categoryHighlights[selectedCatId].items.map((l,i)=><ListingCard key={l.id} listing={l} delay={i*.07}/>)}
                </div>
              </div>
            )}
          </section>
        )}

      </div>

      {/* Listing sections */}
      <div style={{ maxWidth:1280,margin:'0 auto',padding:'0 clamp(16px,4vw,48px)' }}>
        {[
          { eyebrow:'Rent it',   title:'Top Rentals Near You',  items:listings.rent,    link:'View all rentals',  type:'rent' },
          { eyebrow:'Bid on it', title:'Live Auctions',          items:listings.auction, link:'View all auctions', type:'auction' },
          { eyebrow:'Buy it',    title:'For Sale Near You',      items:listings.sale,    link:'View all listings', type:'sale' },
          { eyebrow:'Trade it',  title:'Barter Trades',          items:listings.barter,  link:'View all trades',   type:'barter' },
          { eyebrow:'Take it',   title:'Free Items',             items:listings.free,    link:'View all free',    type:'free' },
        ].map(({ eyebrow, title, items, link, type }) => items.length > 0 && (
          <section key={type} style={{ paddingTop:56 }}>
            <div style={{ display:'flex',alignItems:'flex-end',justifyContent:'space-between',marginBottom:24 }}>
              <div><div className="section-eyebrow">{eyebrow}</div><h2 className="section-title">{title}</h2></div>
              <button type="button" className="tap-target" style={{ fontSize:14,fontWeight:600,color:'var(--cherry)',cursor:'pointer',background:'none',border:'none',padding:0 }} onClick={()=>navigate(type==='barter'?'/barter':type==='free'?'/free':`/browse?type=${type}`)}>{link} →</button>
            </div>
            <div className="listings-grid">
              {items.map((l,i)=><ListingCard key={l.id} listing={l} delay={i*.07}/>)}
            </div>
          </section>
        ))}
      </div>

      {/* ISO / Want Ads strip */}
      <div style={{ maxWidth:1280, margin:'56px auto 0', padding:'0 clamp(16px,4vw,48px)' }}>
        <div style={{ background:'var(--cream)', border:'1.5px solid var(--border)', borderRadius:16, padding:'20px 28px', display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
          <div style={{ fontSize:32, flexShrink:0 }}>🔎</div>
          <div style={{ flex:1, minWidth:200 }}>
            <div style={{ fontWeight:700, fontSize:15, marginBottom:3 }}>Can't find what you're looking for?</div>
            <div style={{ fontSize:13, color:'var(--text-mid)' }}>Post a free Want Ad — local sellers will reach out to you directly when they have what you need.</div>
          </div>
          <button className="btn btn-ghost" style={{ flexShrink:0 }} onClick={() => navigate('/looking-for')}>
            Post a Want Ad →
          </button>
        </div>
      </div>

      {/* How it works */}
      <div className="hiw" style={{ marginTop:56 }}>
        <div style={{ maxWidth:1280,margin:'0 auto' }}>
          <div style={{ textAlign:'center',marginBottom:48 }}>
            <div style={{ fontSize:11,fontWeight:700,letterSpacing:'3px',textTransform:'uppercase',color:'var(--cherry)',marginBottom:12 }}>Simple by design</div>
            <h2 style={{ fontFamily:"'Playfair Display',serif",fontSize:'clamp(28px,4vw,44px)',fontWeight:700,color:'white',letterSpacing:'-1px' }}>How CherryPKD works</h2>
          </div>
          <div className="hiw-grid">
            {hiw.map((s,i)=>(
              <div key={i} className="hiw-card" data-num={s.num}>
                <div style={{ width:48,height:48,background:'var(--cherry)',borderRadius:12,display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,marginBottom:18 }}>{s.icon}</div>
                <h3 style={{ fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,color:'white',marginBottom:10 }}>{s.title}</h3>
                <p style={{ fontSize:13,color:'#888',lineHeight:1.72 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="trust-strip">
        <div className="trust-items">
          {['🔒 Secure escrow','✅ ID verified users','🛡️ Damage deposits','⭐ Rated reviews','📍 Safe meetups', COINBASE_CHECKOUT_ENABLED ? '💳 Card & 🪙 Crypto' : '💳 Secure card payments'].map((t,i)=>(
            <div key={i} style={{ display:'flex',alignItems:'center',gap:0 }}>
              {i>0&&<div style={{ width:1,height:20,background:'rgba(255,255,255,.25)',marginRight:36 }}/>}
              <div className="trust-item">{t}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Suggest a category */}
      <div style={{ maxWidth:1280,margin:'0 auto',padding:'56px clamp(16px,4vw,48px) 0' }}>
        <div style={{ background:'var(--cream)', border:'1.5px dashed var(--cherry)', borderRadius:16, padding:'32px 36px' }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:24, flexWrap:'wrap' }}>
            <div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, marginBottom:6 }}>
                ✨ Don't see your category?
              </h3>
              <p style={{ fontSize:14, color:'var(--text-mid)', lineHeight:1.65, maxWidth:460 }}>
                Suggest a new one. Our team reviews all suggestions and adds them when they reach enough demand.
              </p>
            </div>
            {!showSuggest && !suggestDone && (
              <button className="btn btn-cherry" onClick={() => setShowSuggest(true)}>
                + Suggest a Category
              </button>
            )}
          </div>

          {/* Suggest form */}
          {showSuggest && !suggestDone && (
            <div style={{ marginTop:24, paddingTop:24, borderTop:'1px solid var(--border)' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
                <div className="form-group" style={{ margin:0 }}>
                  <label className="form-label">Category Name *</label>
                  <input
                    className="form-input"
                    style={{ background:'var(--white)', borderStyle:'dashed', borderColor:'var(--cherry)' }}
                    placeholder="e.g. 3D Printing & Maker"
                    value={suggestName}
                    onChange={e => setSuggestName(e.target.value)}
                    maxLength={50}
                  />
                </div>
                <div className="form-group" style={{ margin:0 }}>
                  <label className="form-label">What kind of items?</label>
                  <input
                    className="form-input"
                    placeholder="e.g. 3D printers, filament, maker kits"
                  />
                </div>
              </div>

              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:8 }}>Choose an icon</div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {ICONS.map(ic => (
                    <button
                      key={ic}
                      onClick={() => setSuggestIcon(ic)}
                      style={{ width:36, height:36, borderRadius:8, border:`1.5px solid ${suggestIcon===ic?'var(--cherry)':'var(--border)'}`, background:suggestIcon===ic?'var(--cherry-light)':'white', fontSize:18, cursor:'pointer', transition:'all .15s' }}>
                      {ic}
                    </button>
                  ))}
                </div>
              </div>

              {suggestErr && <div className="alert alert-error" style={{ marginBottom:12 }}>{suggestErr}</div>}

              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <button className="btn btn-cherry" onClick={handleSuggest} disabled={!suggestName.trim()}>
                  Submit Suggestion
                </button>
                <button className="btn btn-ghost" onClick={() => { setShowSuggest(false); setSuggestName(''); setSuggestErr(''); }}>
                  Cancel
                </button>
                {suggestName && (
                  <div style={{ fontSize:13, color:'var(--text-mid)' }}>
                    Preview: <strong>{suggestIcon} {suggestName}</strong>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Success state */}
          {suggestDone && (
            <div style={{ marginTop:20, background:'var(--green-light)', border:'1.5px solid rgba(45,122,58,.2)', borderRadius:12, padding:'14px 18px', display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ fontSize:22 }}>✅</span>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:'var(--green)', marginBottom:2 }}>
                  {suggestIcon} "{suggestName || 'Your suggestion'}" submitted for review!
                </div>
                <div style={{ fontSize:12, color:'var(--text-soft)' }}>
                  We'll add it as an official category once it gets enough demand. Thank you!
                </div>
              </div>
              <button
                className="btn btn-ghost btn-sm"
                style={{ marginLeft:'auto' }}
                onClick={() => { setSuggestDone(false); setShowSuggest(false); setSuggestName(''); setSuggestIcon('📦'); }}>
                Suggest another
              </button>
            </div>
          )}
        </div>
      </div>

      {/* For You — personalized by browsing history */}
      {forYou.length > 0 && (
        <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(16px,4vw,48px) 56px' }}>
          <div style={{ marginBottom:20 }}>
            <div className="section-eyebrow">✨ Picked for you</div>
            <h2 className="section-title">For You</h2>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:18 }}>
            {forYou.map((l, i) => <ListingCard key={l.id} listing={l} delay={i * 0.05} />)}
          </div>
        </div>
      )}

      {/* From Sellers You Follow */}
      {followedSellers.length > 0 && (
        <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(16px,4vw,48px) 56px' }}>
          <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:24 }}>
            <div>
              <div className="section-eyebrow">👥 Your feed</div>
              <h2 className="section-title">From Sellers You Follow</h2>
            </div>
            <button type="button" style={{ fontSize:14, fontWeight:600, color:'var(--cherry)', cursor:'pointer', background:'none', border:'none', padding:0 }} onClick={() => navigate('/browse')}>Browse more →</button>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
            {followedSellers.map(seller => {
              const API_BASE = process.env.REACT_APP_API_URL?.replace('/api','') || '';
              return (
                <div key={seller.id}>
                  <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
                    <a href={`/profile/${seller.id}`} style={{ width:36, height:36, borderRadius:'50%', background:'var(--cherry-light)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:'var(--cherry)', overflow:'hidden', flexShrink:0, textDecoration:'none' }}>
                      {seller.avatar_url
                        ? <img src={seller.avatar_url.startsWith('http') ? seller.avatar_url : `${API_BASE}${seller.avatar_url}`} alt="" loading="lazy" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                        : seller.first_name?.[0]?.toUpperCase()}
                    </a>
                    <div>
                      <a href={`/profile/${seller.id}`} style={{ fontWeight:700, fontSize:14, color:'var(--text-primary)', textDecoration:'none' }}>
                        {seller.first_name} {seller.last_name}
                      </a>
                      {seller.rating_avg > 0 && <span style={{ fontSize:11, color:'var(--text-soft)', marginLeft:8 }}>⭐ {parseFloat(seller.rating_avg).toFixed(1)}</span>}
                      {seller.id_verified && <span style={{ fontSize:10, fontWeight:700, color:'#1A5276', marginLeft:8 }}>✅ Verified</span>}
                    </div>
                    <button className="btn btn-ghost btn-sm" style={{ marginLeft:'auto' }} onClick={() => navigate(`/profile/${seller.id}`)}>View Shop →</button>
                  </div>
                  <div style={{ display:'flex', gap:12, overflowX:'auto', paddingBottom:8, scrollbarWidth:'thin' }}>
                    {seller.recent_listings.map(l => {
                      const lColor = l.listing_type==='rent'?'#1A5276':l.listing_type==='auction'?'#C0392B':l.listing_type==='barter'?'#7B2D8B':l.listing_type==='free'?'#0E7490':'#2D7A3A';
                      const lBadge = l.listing_type==='rent'?'Rent':l.listing_type==='auction'?'Auction':l.listing_type==='barter'?'Barter':l.listing_type==='free'?'Free':'Sale';
                      return (
                        <a key={l.id} href={`/listing/${l.id}`} style={{ flexShrink:0, width:180, background:'var(--white)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden', cursor:'pointer', transition:'transform .15s,box-shadow .15s', textDecoration:'none', color:'inherit', display:'block' }}
                          onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-3px)';e.currentTarget.style.boxShadow='var(--shadow-md)';}}
                          onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='';}}>
                          <div style={{ height:120, background:'var(--cream)', overflow:'hidden', position:'relative', display:'flex', alignItems:'center', justifyContent:'center' }}>
                            {l.primary_image
                              ? <img src={l.primary_image.startsWith('http') ? l.primary_image : `${API_BASE}${l.primary_image}`} alt={l.title} loading="lazy" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                              : <span style={{ fontSize:36, opacity:.4 }}>📦</span>
                            }
                            <div style={{ position:'absolute', top:6, left:6, background:lColor, color:'white', fontSize:9, fontWeight:700, padding:'2px 6px', borderRadius:5, letterSpacing:'1px', textTransform:'uppercase' }}>{lBadge}</div>
                          </div>
                          <div style={{ padding:'10px 12px' }}>
                            <div style={{ fontSize:12, fontWeight:600, lineHeight:1.3, marginBottom:5, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{l.title}</div>
                            <div style={{ fontWeight:700, fontSize:14, color: l.listing_type==='free'?'#0E7490':l.listing_type==='barter'?'#7B2D8B':'var(--cherry)' }}>
                              {l.listing_type==='free' ? 'Free' : l.listing_type==='barter' ? 'Trade' : `$${parseFloat(l.price||0).toLocaleString()}`}
                            </div>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recently Viewed */}
      {recentlyViewed.length > 0 && (
        <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(16px,4vw,48px) 56px' }}>
          <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:20 }}>
            <div>
              <div className="section-eyebrow">👁 Your Activity</div>
              <h2 className="section-title">Recently Viewed</h2>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => { localStorage.removeItem('cherrypkd_rv'); setRecentlyViewed([]); }}>Clear history</button>
          </div>
          <div style={{ display:'flex', gap:14, overflowX:'auto', paddingBottom:12, scrollbarWidth:'thin' }}>
            {recentlyViewed.map(l => {
              const rvColor = l.listing_type==='rent'?'#1A5276':l.listing_type==='auction'?'#C0392B':l.listing_type==='barter'?'#7B2D8B':l.listing_type==='free'?'#0E7490':'#2D7A3A';
              const rvBadge = l.listing_type==='rent'?'For Rent':l.listing_type==='auction'?'Auction':l.listing_type==='barter'?'Barter':l.listing_type==='free'?'Free':'For Sale';
              const rvPrice = l.listing_type==='auction' ? (l.current_bid||l.starting_bid||0) : l.price;
              const API_BASE = process.env.REACT_APP_API_URL?.replace('/api','') || '';
              return (
                <a key={l.id} href={`/listing/${l.slug||l.id}`}
                  style={{ flexShrink:0, width:160, background:'var(--white)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden', cursor:'pointer', transition:'transform .15s,box-shadow .15s', textDecoration:'none', color:'inherit', display:'block' }}
                  onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-3px)';e.currentTarget.style.boxShadow='var(--shadow-md)';}}
                  onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='';}}>
                  <div style={{ height:110, background:'var(--cream)', overflow:'hidden', position:'relative', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {l.primary_image
                      ? <img src={l.primary_image.startsWith('http')?l.primary_image:`${API_BASE}${l.primary_image}`} alt={l.title} loading="lazy" style={{ width:'100%',height:'100%',objectFit:'cover', filter: rvActiveIds && !rvActiveIds.has(l.id) ? 'grayscale(0.6)' : 'none' }} />
                      : <span style={{ fontSize:36, opacity:.4 }}>📦</span>
                    }
                    <div style={{ position:'absolute', top:6, left:6, background:rvColor, color:'white', fontSize:9, fontWeight:700, padding:'2px 6px', borderRadius:5, letterSpacing:'1px', textTransform:'uppercase' }}>{rvBadge}</div>
                    {rvActiveIds && !rvActiveIds.has(l.id) && (
                      <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <span style={{ background:'rgba(0,0,0,0.75)', color:'white', fontSize:11, fontWeight:800, padding:'4px 10px', borderRadius:6, letterSpacing:'1.5px', textTransform:'uppercase' }}>Sold</span>
                      </div>
                    )}
                  </div>
                  <div style={{ padding:'10px 12px' }}>
                    <div style={{ fontSize:12, fontWeight:600, lineHeight:1.3, marginBottom:5, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{l.title}</div>
                    <div style={{ fontWeight:700, fontSize:14, color: l.listing_type==='free'?'#0E7490':l.listing_type==='barter'?'#7B2D8B':'var(--cherry)' }}>
                      {l.listing_type==='free' ? 'Free' : l.listing_type==='barter' ? 'Trade' : `$${parseFloat(rvPrice||0).toLocaleString()}`}
                      {l.listing_type==='rent' && <span style={{ fontSize:10, fontWeight:400, color:'var(--text-soft)' }}>/day</span>}
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Sell banner */}
      <div style={{ maxWidth:1280,margin:'0 auto',padding:'48px clamp(16px,4vw,48px) 64px' }}>
        <div className="grid-cta-banner" style={{ background:'var(--cream)',borderRadius:20,border:'1px solid var(--border)',padding:'40px clamp(20px,4vw,56px)',position:'relative',overflow:'hidden' }}>
          <div style={{ position:'absolute',right:260,top:-20,fontSize:160,opacity:.05,transform:'rotate(-15deg)',pointerEvents:'none' }}>🍒</div>
          <div>
            <h2 style={{ fontFamily:"'Playfair Display',serif",fontSize:'clamp(26px,3vw,36px)',fontWeight:700,letterSpacing:'-.5px',marginBottom:12 }}>
              Got stuff? <em style={{ color:'var(--cherry)',fontStyle:'italic' }}>Make money.</em>
            </h2>
            <p style={{ fontSize:15,color:'var(--text-mid)',lineHeight:1.65,maxWidth:480,marginBottom:20 }}>List for rent, auction, or sale in under 5 minutes. Free to list — we only take a fee on completed deals: 8% (minimum $1.00 per order, never more than the item price).</p>
            <div style={{ display:'flex',gap:20,flexWrap:'wrap' }}>
              {['Free to list','8% fee, min $1.00 per order','Damage deposits built in','Paid 3 days after delivery'].map(p=>(
                <div key={p} style={{ display:'flex',alignItems:'center',gap:6,fontSize:13,fontWeight:600,color:'var(--text-mid)' }}>
                  <div style={{ width:6,height:6,background:'var(--cherry)',borderRadius:'50%' }}/>{p}
                </div>
              ))}
            </div>
          </div>
          <div style={{ display:'flex',flexDirection:'column',gap:10,alignItems:'center',flexShrink:0 }}>
            <button className="btn btn-cherry btn-lg" onClick={()=>navigate('/list')}>Start listing today</button>
            <span style={{ fontSize:11,color:'var(--text-soft)' }}>No subscription · No upfront fees</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  LoginPage.jsx
// ══════════════════════════════════════════════════════════════════
export function LoginPage() {
  useSEO({ title: 'Log in' });
  const navigate = useNavigate();
  // UX review: while the invite gate is on for this visitor, sign-up starts with an invite code.
  const { locked: gateLocked, enterInvite } = useInviteGate();
  const [searchParams] = useSearchParams();
  const { login, completeLogin, updateUser } = useAuth();
  const [form, setForm] = useState({ email:'', password:'' });
  const [error, setError] = useState('');
  const [isBanned, setIsBanned] = useState(false);
  const [appealSent, setAppealSent] = useState(false);
  const [appealMsg, setAppealMsg] = useState('');
  const [appealLoading, setAppealLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requiresSecondFactor, setRequiresSecondFactor] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [passkeyEnabled, setPasskeyEnabled] = useState(false);

  // Handle OAuth redirect callback — backend sets cookie, we just load the user
  useEffect(() => {
    const oauthError = searchParams.get('error');
    // r7: a new Google account without an invite is bounced here by the server-side gate.
    if (oauthError === 'invite_required') { setError('CherryPKD is invite-only during the soft launch. Enter your invite code first, then sign up with Google.'); return; }
    if (oauthError) { setError('Sign-in failed — please try again'); return; }
    if (searchParams.get('provider')) {
      API.get('/auth/me').then(r => {
        updateUser(r.data.user);
        navigate(getSafeRedirect(searchParams), { replace: true });
      }).catch(() => setError('Social sign-in failed'));
    }
  }, []); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await API.post('/auth/login', { email: form.email, password: form.password });

      if (res.data.requiresSecondFactor) {
        // User has 2FA enabled, show second factor prompt
        setTempToken(res.data.tempToken);
        setTwoFaEnabled(res.data.twoFaEnabled);
        setPasskeyEnabled(res.data.passkeyEnabled);
        setRequiresSecondFactor(true);
      } else {
        // No 2FA: this response already signed us in (cookies + user), so reuse it rather than
        // POSTing /auth/login a second time. Fall back to login() if the body lacks the user.
        if (res.data.user && completeLogin) completeLogin(res.data.user);
        else await login(form.email, form.password);
        const redirect = getSafeRedirect(searchParams);
        navigate(redirect);
      }
    } catch (err) {
      if (err.response?.data?.banned) {
        setIsBanned(true);
        setError(err.response.data.error || 'Account suspended.');
      } else {
        setError(err.response?.data?.error || 'Login failed');
      }
    } finally { setLoading(false); }
  };

  const submitAppeal = async () => {
    if (!appealMsg.trim()) return;
    setAppealLoading(true);
    try {
      await API.post('/auth/appeal', { email: form.email, message: appealMsg });
      setAppealSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send appeal');
    }
    setAppealLoading(false);
  };

  const handleSecondFactorSuccess = (data) => {
    updateUser(data.user);
    navigate(getSafeRedirect(searchParams));
  };

  const handleSecondFactorCancel = () => {
    setRequiresSecondFactor(false);
    setTempToken('');
    setForm({ email: '', password: '' });
  };

  if (requiresSecondFactor) {
    return (
      <SecondFactorPrompt
        tempToken={tempToken}
        email={form.email}
        twoFaEnabled={twoFaEnabled}
        passkeyEnabled={passkeyEnabled}
        onSuccess={handleSecondFactorSuccess}
        onCancel={handleSecondFactorCancel}
      />
    );
  }

  return (
    <div className="auth-page" style={{ minHeight:'calc(100vh - 68px)',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--cream)',padding:24 }}>
      <div style={{ background:'var(--white)',borderRadius:20,padding:'clamp(20px,5vw,48px)',width:'100%',maxWidth:440,boxShadow:'var(--shadow-lg)' }}>
        <div style={{ textAlign:'center',marginBottom:32 }}>
          <div style={{ fontSize:36,marginBottom:12 }}>🍒</div>
          <h1 style={{ fontFamily:"'Playfair Display',serif",fontSize:28,fontWeight:700,marginBottom:6 }}>Welcome back</h1>
          <p style={{ fontSize:14,color:'var(--text-soft)' }}>Log in to your CherryPKD account</p>
        </div>

        {isBanned ? (
          <div style={{ background:'#FFF3E0', border:'2px solid #FF9800', borderRadius:14, padding:20, marginBottom:16 }}>
            <div style={{ fontWeight:700, fontSize:15, marginBottom:8, color:'#E65100' }}>🚫 Account Suspended</div>
            <p style={{ fontSize:13, color:'var(--text-mid)', marginBottom:12 }}>Your account has been suspended. If you believe this is a mistake, you can submit an appeal below.</p>
            {appealSent ? (
              <div style={{ fontSize:13, color:'#2D7A3A', fontWeight:600 }}>✅ Appeal submitted — we'll review it within 48 hours and email you at {form.email}.</div>
            ) : (
              <>
                <textarea className="form-input" rows={3} placeholder="Explain why you believe the suspension was in error..." value={appealMsg} onChange={e => setAppealMsg(e.target.value)} style={{ marginBottom:10, resize:'vertical' }} />
                <button className="btn btn-cherry btn-full" disabled={appealLoading || !appealMsg.trim()} onClick={submitAppeal}>
                  {appealLoading ? 'Sending...' : 'Submit Appeal'}
                </button>
              </>
            )}
          </div>
        ) : (
          error && <div className="alert alert-error" style={{ marginBottom:16 }}>{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" placeholder="you@example.com" autoComplete="email" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} required />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input className="form-input" type="password" placeholder="••••••••" value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} autoComplete="current-password" required />
            <div style={{ textAlign:'right',marginTop:4 }}>
              <button type="button" style={{ fontSize:12,color:'var(--cherry)',cursor:'pointer',background:'none',border:'none',padding:0,display:'inline-flex',alignItems:'center',minHeight:40,minWidth:40 }} onClick={()=>navigate('/forgot-password')}>Forgot password?</button>
            </div>
          </div>
          <button type="submit" className="btn btn-cherry btn-full btn-lg" disabled={loading}>
            {loading ? <><span className="spinner"/> Logging in...</> : 'Log in'}
          </button>
        </form>

        <SocialLogin
          onSuccess={(data) => {
            updateUser(data.user);
            navigate(getSafeRedirect(searchParams));
          }}
          onError={setError}
        />

        <div style={{ textAlign:'center',marginTop:20,fontSize:14,color:'var(--text-soft)' }}>
          Don't have an account?{' '}
          {gateLocked ? (
            <>
              <span style={{ display:'inline-block',padding:'2px 8px',borderRadius:100,background:'var(--cream)',border:'1px solid var(--border)',fontSize:12,fontWeight:700,color:'var(--text-mid)',verticalAlign:'middle' }}>Invite required</span>{' '}
              <button type="button" style={{ color:'var(--cherry)',fontWeight:600,cursor:'pointer',background:'none',border:'none',padding:0,display:'inline-flex',alignItems:'center',verticalAlign:'middle',minHeight:40,minWidth:40 }} onClick={enterInvite}>Enter invite code</button>
            </>
          ) : (
            <button type="button" style={{ color:'var(--cherry)',fontWeight:600,cursor:'pointer',background:'none',border:'none',padding:0,display:'inline-flex',alignItems:'center',verticalAlign:'middle',minHeight:40,minWidth:40 }} onClick={()=>navigate(authPathWithRedirect('/signup', searchParams))}>Get started</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  SignupPage.jsx
// ══════════════════════════════════════════════════════════════════
export function SignupPage() {
  useSEO({ title: 'Create account' });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signup, updateUser } = useAuth();
  const refCode = searchParams.get('ref') || '';
  // ?email= (from checkout's guest screens) pre-fills the address; ?name= (guest order confirmation)
  // pre-fills first / last name from the name the guest typed at checkout.
  const [form, setForm] = useState(() => {
    const nameParts = (searchParams.get('name') || '').trim().slice(0, 100).split(/\s+/).filter(Boolean);
    return { firstName: nameParts[0] || '', lastName: nameParts.slice(1).join(' '), username:'',email:(searchParams.get('email') || '').trim().slice(0, 254),password:'',confirmPassword:'',locationCity:'',locationState:'',locationZip:'' };
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [referrerName, setReferrerName] = useState('');
  // r7: the server enforces the soft-launch invite gate on signup. A visitor who redeemed a
  // code already carries the pass cookie; anyone else (or a pass from before r7) gets
  // INVITE_REQUIRED and can type the code here. ?invite=CODE pre-fills it.
  const [inviteCode, setInviteCode] = useState(() => (searchParams.get('invite') || '').trim().slice(0, 64));
  const [showInvite, setShowInvite] = useState(() => !!searchParams.get('invite'));

  useEffect(() => {
    if (refCode) {
      API.get(`/referrals/validate/${refCode}`)
        .then(r => { if (r.data.valid) setReferrerName(r.data.referrerName); })
        .catch(() => {});
    }
  }, [refCode]); // eslint-disable-line

  const validate = () => {
    const e = {};
    if (!form.firstName)      e.firstName     = 'Required';
    if (!form.lastName)       e.lastName      = 'Required';
    if (!form.email)          e.email         = 'Required';
    if (form.password.length < 8) e.password  = 'Min 8 characters';
    if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords don\'t match';
    if (form.username && form.username.length < 3) e.username = 'Min 3 characters';
    if (!form.locationCity)   e.locationCity  = 'Required';
    if (!form.locationState)  e.locationState = 'Required';
    if (!form.locationZip)    e.locationZip   = 'Required';
    setErrors(e); return !Object.keys(e).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await signup({ firstName:form.firstName, lastName:form.lastName, username:form.username, email:form.email, password:form.password, locationCity:form.locationCity, locationState:form.locationState, locationZip:form.locationZip, referralCode: refCode || undefined, inviteCode: inviteCode.trim() || undefined });
      const redirect = getSafeRedirect(searchParams);
      navigate(redirect);
    } catch (err) {
      if (err.response?.data?.code === 'INVITE_REQUIRED') {
        setShowInvite(true);
        setErrors({ submit: inviteCode.trim() ? 'That invite code is invalid or has been used up.' : 'CherryPKD is invite-only during the soft launch. Enter your invite code to create your account.', inviteCode: inviteCode.trim() ? 'Check the code' : 'Required' });
        return;
      }
      setErrors({ submit: err.response?.data?.error || 'Signup failed' });
    } finally { setLoading(false); }
  };

  const f = (name) => ({
    className: `form-input ${errors[name] ? 'error' : ''}`,
    value: form[name],
    onChange: e => setForm(p => ({ ...p, [name]: e.target.value })),
  });

  return (
    <div style={{ minHeight:'calc(100vh - 68px)',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--cream)',padding:24 }}>
      <div style={{ background:'var(--white)',borderRadius:20,padding:'clamp(20px,5vw,48px)',width:'100%',maxWidth:600,boxShadow:'var(--shadow-lg)' }}>
        <div style={{ textAlign:'center',marginBottom:32 }}>
          <div style={{ fontSize:36,marginBottom:12 }}>🍒</div>
          <h1 style={{ fontFamily:"'Playfair Display',serif",fontSize:28,fontWeight:700,marginBottom:6 }}>Create your account</h1>
          <p style={{ fontSize:14,color:'var(--text-soft)' }}>Free to join. Start renting, bidding and selling today.</p>
        </div>

        {/* Free Account Benefits */}
        <div style={{ background:'#F1F8E9',borderRadius:12,padding:16,marginBottom:24 }}>
          <div style={{ fontSize:12,fontWeight:700,color:'#4CAF50',marginBottom:8,textTransform:'uppercase' }}>✓ Included with free account</div>
          <ul style={{ fontSize:13,color:'var(--text-mid)',margin:0,paddingLeft:20,lineHeight:1.8 }}>
            <li>Browse all items (rent, buy, auction)</li>
            <li>Claim free items</li>
            <li>Make purchases & rentals</li>
            <li>Bid on auctions</li>
          </ul>
        </div>

        {referrerName && (
          <div style={{ background:'#FFF3E0', border:'1px solid #FFB74D', borderRadius:10, padding:'12px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:22 }}>🎁</span>
            <div>
              <div style={{ fontWeight:700, fontSize:14 }}>You were invited by {referrerName}!</div>
              <div style={{ fontSize:12, color:'var(--text-mid)' }}>Sign up below to join CherryPKD together.</div>
            </div>
          </div>
        )}

        {errors.submit && <div className="alert alert-error" style={{ marginBottom:16 }}>{errors.submit}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
            <div className="form-group">
              <label className="form-label">First Name<span className="required"> *</span></label>
              <input {...f('firstName')} placeholder="John" autoComplete="given-name" />
              {errors.firstName && <div className="form-error">{errors.firstName}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Last Name<span className="required"> *</span></label>
              <input {...f('lastName')} placeholder="Smith" autoComplete="family-name" />
              {errors.lastName && <div className="form-error">{errors.lastName}</div>}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Username <span style={{ color:'var(--text-soft)', fontSize:12 }}>(optional)</span></label>
            <input {...f('username')} placeholder="johnsmith123" autoComplete="username" />
            {errors.username && <div className="form-error">{errors.username}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Email<span className="required"> *</span></label>
            <input {...f('email')} type="email" placeholder="you@example.com" autoComplete="email" />
            {errors.email && <div className="form-error">{errors.email}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Password<span className="required"> *</span></label>
            <input {...f('password')} type="password" placeholder="Min 8 characters" autoComplete="new-password" />
            {errors.password && <div className="form-error">{errors.password}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Confirm Password<span className="required"> *</span></label>
            <input {...f('confirmPassword')} type="password" placeholder="Repeat password" autoComplete="new-password" />
            {errors.confirmPassword && <div className="form-error">{errors.confirmPassword}</div>}
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,gridAutoFlow:'dense' }} className="city-state-zip-grid">
            <div className="form-group">
              <label className="form-label">City<span className="required"> *</span></label>
              <input {...f('locationCity')} placeholder="City" autoComplete="address-level2" />
              {errors.locationCity && <div className="form-error">{errors.locationCity}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">State<span className="required"> *</span></label>
              <input {...f('locationState')} placeholder="State" autoComplete="address-level1" />
              {errors.locationState && <div className="form-error">{errors.locationState}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Zip<span className="required"> *</span></label>
              <input {...f('locationZip')} placeholder="Zip" inputMode="numeric" autoComplete="postal-code" />
              {errors.locationZip && <div className="form-error">{errors.locationZip}</div>}
            </div>
          </div>

          {showInvite && (
            <div className="form-group">
              <label className="form-label" htmlFor="signup-invite-code">Invite code<span className="required"> *</span></label>
              <input id="signup-invite-code" className={`form-input ${errors.inviteCode ? 'error' : ''}`} value={inviteCode}
                onChange={e => setInviteCode(e.target.value.slice(0, 64))} placeholder="e.g. CHERRY-XXXX" autoComplete="off" autoCapitalize="characters" spellCheck={false} />
              {errors.inviteCode && <div className="form-error">{errors.inviteCode}</div>}
            </div>
          )}

          <div style={{ fontSize:12,color:'var(--text-soft)',marginBottom:20,lineHeight:1.6 }}>
            By creating an account you agree to our{' '}
            <a style={{ color:'var(--cherry)',cursor:'pointer' }}>Terms of Service</a> and{' '}
            <a style={{ color:'var(--cherry)',cursor:'pointer' }}>Privacy Policy</a>.
          </div>

          <button type="submit" className="btn btn-cherry btn-full btn-lg" disabled={loading}>
            {loading ? <><span className="spinner"/> Creating account...</> : 'Create free account'}
          </button>
        </form>

        <SocialLogin
          onSuccess={(data) => {
            updateUser(data.user);
            navigate(getSafeRedirect(searchParams));
          }}
          onError={(msg) => setErrors({ submit: msg })}
        />

        <div style={{ textAlign:'center',marginTop:20,fontSize:14,color:'var(--text-soft)' }}>
          Already have an account?{' '}
          <button type="button" style={{ color:'var(--cherry)',fontWeight:600,cursor:'pointer',background:'none',border:'none',padding:0,display:'inline-flex',alignItems:'center',verticalAlign:'middle',minHeight:40,minWidth:40 }} onClick={()=>navigate(authPathWithRedirect('/login', searchParams))}>Log in</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  ForgotPasswordPage.jsx + ResetPasswordPage.jsx
// ══════════════════════════════════════════════════════════════════
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent]   = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await API.post('/auth/forgot-password', { email }); setSent(true); }
    catch {} finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:'calc(100vh - 68px)',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--cream)',padding:24 }}>
      <div style={{ background:'var(--white)',borderRadius:20,padding:'clamp(20px,5vw,48px)',width:'100%',maxWidth:440,boxShadow:'var(--shadow-lg)',textAlign:'center' }}>
        <div style={{ fontSize:36,marginBottom:16 }}>🔑</div>
        <h1 style={{ fontFamily:"'Playfair Display',serif",fontSize:26,fontWeight:700,marginBottom:8 }}>Reset your password</h1>
        {sent ? (
          <div>
            <div className="alert alert-success" style={{ marginBottom:20 }}>✅ Check your email for a reset link</div>
            <button className="btn btn-ghost" onClick={()=>navigate('/login')}>Back to login</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <p style={{ fontSize:14,color:'var(--text-soft)',marginBottom:20 }}>Enter your email and we'll send you a reset link.</p>
            <div className="form-group" style={{ textAlign:'left' }}>
              <label className="form-label">Email</label>
              <input className="form-input" type="email" placeholder="you@example.com" autoComplete="email" value={email} onChange={e=>setEmail(e.target.value)} required />
            </div>
            <button type="submit" className="btn btn-cherry btn-full" disabled={loading}>
              {loading ? <><span className="spinner"/> Sending...</> : 'Send reset link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [form, setForm] = useState({ password:'', confirm:'' });
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (form.password !== form.confirm) { setError("Passwords don't match"); return; }
    if (!token) { setError('Invalid reset link — please request a new one'); return; }
    setLoading(true);
    try {
      await API.post('/auth/reset-password', { token, password: form.password });
      setDone(true);
    } catch (err) { setError(err.response?.data?.error || 'Reset failed — the link may have expired'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:'calc(100vh - 68px)',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--cream)',padding:24 }}>
      <div style={{ background:'var(--white)',borderRadius:20,padding:'clamp(20px,5vw,48px)',width:'100%',maxWidth:440,boxShadow:'var(--shadow-lg)',textAlign:'center' }}>
        <div style={{ fontSize:36,marginBottom:16 }}>🔒</div>
        <h1 style={{ fontFamily:"'Playfair Display',serif",fontSize:26,fontWeight:700,marginBottom:20 }}>Set new password</h1>

        {!token && !done && (
          <div>
            <div className="alert alert-error" style={{ marginBottom:20 }}>Invalid or missing reset link. Please request a new one.</div>
            <button className="btn btn-cherry" onClick={()=>navigate('/forgot-password')}>Request new link</button>
          </div>
        )}

        {token && done ? (
          <div>
            <div className="alert alert-success" style={{ marginBottom:20 }}>✅ Password updated! You can now log in.</div>
            <button className="btn btn-cherry" onClick={()=>navigate('/login')}>Go to login</button>
          </div>
        ) : token && (
          <form onSubmit={handleSubmit} style={{ textAlign:'left' }}>
            {error && <div className="alert alert-error" style={{ marginBottom:14 }}>{error}</div>}
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input className="form-input" type="password" placeholder="Min 8 characters" value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} autoComplete="new-password" required />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input className="form-input" type="password" placeholder="Repeat password" value={form.confirm} onChange={e=>setForm(p=>({...p,confirm:e.target.value}))} autoComplete="new-password" required />
            </div>
            <button type="submit" className="btn btn-cherry btn-full" disabled={loading}>
              {loading ? <><span className="spinner"/> Updating...</> : 'Update password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
